import { useState, useEffect, useRef, useCallback } from "react";
import { makeRequest } from "../utils/request";

type PaginatedResponse<T> = {
  data: T[];
  metadata: {
    total: number;
    page: number;
    limit: number;
  };
};

type UseInfiniteScrollOptions<T> = {
  url: string;
  limit?: number;
  sort?: string;
  getItemId: (item: T) => string;
};

export const useInfiniteScroll = <T>({ url, limit = 100, sort, getItemId }: UseInfiniteScrollOptions<T>) => {
  const [items, setItems] = useState<T[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  const isFetchingRef = useRef(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const sentinelNodeRef = useRef<HTMLDivElement | null>(null);
  const currentUrlRef = useRef(url);

  const fetchPage = useCallback(
    async (pageNum: number, isFirstPage: boolean) => {
      if (isFetchingRef.current) return;
      isFetchingRef.current = true;

      if (isFirstPage) {
        setIsLoading(true);
      } else {
        setIsLoadingMore(true);
      }

      try {
        const params = new URLSearchParams();
        params.set("page", String(pageNum));
        params.set("limit", String(limit));
        if (sort) params.set("sort", sort);

        const separator = url.includes("?") ? "&" : "?";
        const fullUrl = `${url}${separator}${params.toString()}`;
        const response = await makeRequest<PaginatedResponse<T>>(fullUrl);

        // Abort if URL changed while fetching (session switch)
        if (currentUrlRef.current !== url) return;

        const newItems = response.data;
        const meta = response.metadata;

        setTotal(meta.total);
        setPage(meta.page);
        setHasMore(meta.page * meta.limit < meta.total);

        if (isFirstPage) {
          setItems(newItems);
        } else {
          setItems((prev) => {
            const existingIds = new Set(prev.map(getItemId));
            const deduped = newItems.filter((item) => !existingIds.has(getItemId(item)));
            return [...prev, ...deduped];
          });
        }
      } catch {
        // Silently handle errors - loading states will reset
      } finally {
        if (isFirstPage) {
          setIsLoading(false);
        } else {
          setIsLoadingMore(false);
        }
        isFetchingRef.current = false;
      }
    },
    [url, limit, sort, getItemId],
  );

  // Reset and fetch page 1 when URL changes
  useEffect(() => {
    currentUrlRef.current = url;
    setItems([]);
    setPage(1);
    setTotal(0);
    setHasMore(false);
    isFetchingRef.current = false;
    if (url) {
      fetchPage(1, true);
    }
  }, [url]); // eslint-disable-line

  const loadMore = useCallback(() => {
    if (!hasMore || isFetchingRef.current) return;
    fetchPage(page + 1, false);
  }, [hasMore, page, fetchPage]);

  // Callback ref for the sentinel div — re-attaches observer on mount/unmount (tab switches)
  const sentinelRef = useCallback(
    (node: HTMLDivElement | null) => {
      // Disconnect existing observer
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }

      sentinelNodeRef.current = node;

      if (node) {
        observerRef.current = new IntersectionObserver(
          (entries) => {
            if (entries[0]?.isIntersecting) {
              loadMore();
            }
          },
          { rootMargin: "200px" },
        );
        observerRef.current.observe(node);
      }
    },
    [loadMore],
  );

  // Cleanup observer on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  return {
    items,
    isLoading,
    isLoadingMore,
    hasMore,
    total,
    sentinelRef,
  };
};
